import type { ReactNode } from "react"
import { createContext, use, useEffect, useMemo, useState } from "react"

type ColorScheme = "dark" | "light"

type ThemeProviderProps = {
  children: ReactNode
}

type ThemeProviderState = {
  colorScheme: ColorScheme
}

const initialState: ThemeProviderState = {
  colorScheme: "light",
}

const ThemeProviderContext = createContext(initialState)

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const [systemTheme, setSystemTheme] = useState<ColorScheme>(() => {
    if (typeof window === "undefined") {
      return "light"
    }

    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light"
  })

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = () => {
      setSystemTheme(media.matches ? "dark" : "light")
    }

    media.addEventListener("change", onChange)

    return () => {
      media.removeEventListener("change", onChange)
    }
  }, [])

  const colorScheme = systemTheme

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")
    root.classList.add(colorScheme)
  }, [colorScheme])

  const value = useMemo(
    () => ({
      colorScheme,
    }),
    [colorScheme],
  )

  return (
    <ThemeProviderContext {...props} value={value}>
      {children}
    </ThemeProviderContext>
  )
}

export const useTheme = () => use(ThemeProviderContext)
